import { useEffect, useRef, useState } from "react";
import { FileSearch } from "lucide-react";
import type { AiProposal, Citation } from "@ncp/api-client";
import { useTranslation } from "@ncp/i18n";
import { SourceDrawer, SourceText, cn } from "@ncp/ui";
import { useMarkOpened } from "./ProposalReview";

type Readable = Pick<AiProposal, "id" | "status" | "caseId">;

/**
 * The end of a draft's text, where a tab lays the text out: once it has been on screen, the draft counts as read to its end (Condition 3.10),
 * as on the shared review card – never on loading the tab. Renders nothing visible.
 */
export function DraftTextEnd({ proposal, enabled = true }: { proposal: Readable | null | undefined; enabled?: boolean }) {
  const markOpened = useMarkOpened();
  const ref = useRef<HTMLDivElement>(null);
  const read = useRef(markOpened);
  read.current = markOpened;
  const active = !!proposal && proposal.status === "Proposed" && enabled;
  useEffect(() => {
    const end = ref.current;
    if (!active || !end || !proposal) return;
    if (typeof IntersectionObserver === "undefined") { read.current(proposal, { sources: false }); return; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { read.current(proposal, { sources: false }); observer.disconnect(); }
    });
    observer.observe(end);
    return () => observer.disconnect();
  }, [active, proposal?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={ref} aria-hidden className="h-px w-full" data-draft-text-end={proposal?.id} />;
}

/**
 * The sources a draft cites, each opened in the source drawer: opening one is the reading of its sources the approval rests on (Condition 3.10),
 * as on the shared review card. Nothing when the draft cites none.
 */
export function DraftSources({ proposal, className }: { proposal: (Readable & Pick<AiProposal, "citations">) | null | undefined; className?: string }) {
  const { t } = useTranslation();
  const markOpened = useMarkOpened();
  const [open, setOpen] = useState<Citation | null>(null);
  const citations = proposal?.citations ?? [];
  if (!proposal || citations.length === 0) return null;
  return (
    <section className={cn("rounded-xl bg-sand-50 p-3", className)} data-draft-sources={citations.length}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-sand-600"><FileSearch className="size-3.5" aria-hidden /> {t("ai.review.draftSources")}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{t("ai.review.draftSourcesHint")}</p>
      <ul className="mt-1.5 space-y-0.5 text-xs">
        {citations.map((c) => (
          <li key={c.id}>
            <button type="button" className="text-start underline-offset-4 hover:underline" onClick={() => { setOpen(c); markOpened(proposal, { sources: true }); }}>
              <SourceText text={c.title} />{c.locator ? <span className="text-muted-foreground"> · <SourceText text={c.locator} /></span> : null}
            </button>
          </li>
        ))}
      </ul>
      <SourceDrawer citation={open} onClose={() => setOpen(null)} />
    </section>
  );
}
